import React, { startTransition, useMemo, useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import FlowchartView from '../FlowchartView';
import TableView from '../TableView';
import KanbanView from '../KanbanView';
import GanttView from '../GanttView';
import TodoListView from '../TodoListView';
import NeuralView from '../NeuralView';
import BoardView from '../BoardView';
import BulkActionsToolbar from './BulkActionsToolbar';
import LogoLoader from '../../ui/LogoLoader';
import { useDocSheetOpen } from '../../../utils/docSheetPresence';
import { applyFilters } from '../shared/filterUtils';
import { paintBoardSelection } from '../BoardView/utils/paintBoardSelection';

function boardHasFilters(filters) {
  if (!filters) return false;
  const hasArrayFilters = Object.entries(filters).some(([key, value]) => (
    key !== 'customFields' && Array.isArray(value) && value.length > 0
  ));
  const hasCustomFieldFilters = filters.customFields && Object.values(filters.customFields).some((criteria) => (
    criteria && (
      (criteria.values && criteria.values.length > 0)
      || (criteria.search && criteria.search.length > 0)
      || (criteria.operator && criteria.value !== undefined && criteria.value !== '')
    )
  ));
  return hasArrayFilters || hasCustomFieldFilters;
}

/**
 * ViewContainer
 * 
 * Renders the appropriate view based on currentView.
 * Keeps view-specific prop assembly close to view usage.
 * Includes shared BulkActionsToolbar for multi-selection across all views.
 */
const ViewContainer = ({
  isLoadingContent = false,
  currentView,
  
  // Common data
  tasks,
  statuses,
  localSprint,
  calculatedProgress,
  showArchived,
  showActionableOnly,
  showNestedCampaigns = true,
  boardFilters = {},
  
  // View-specific props (pre-assembled)
  flowProps,
  kanbanProps,
  tableProps,
  
  // Gantt-specific
  ganttSwimlane,
  ganttSortSetting = 'manual',
  ganttFilters = {},
  breadcrumbPath,
  onSelectSprint,
  onSprintIconChange,
  onRenameSprint,
  onTaskDateRangeChange,
  onOpenDetails,
  allTags,
  customFields = [],
  
  // Selection state for each view (from useSprintDetailState)
  kanbanSelectedIds,
  onKanbanSelectionChange,
  tableSelectedIds,
  onTableSelectionChange,
  ganttSelectedIds,
  onGanttSelectionChange,
  todoSelectedIds,
  onTodoSelectionChange,
  neuralSelectedIds,
  onNeuralSelectionChange,
  
  // Gantt Fokus mode
  ganttFokus,
  // Gantt add task/decision handlers
  onGanttAddTask,
  onGanttAddDecision,
  onGanttAddWait,
  // Gantt decision handlers
  onGanttDecisionAnswerChange,
  onGanttDecisionOptionsChange,
  // Gantt rename handler
  onGanttRenameTask,
  // Gantt delete handler (for DEL/Backspace key)
  onGanttDeleteTasks,
  // Gantt paste handler (for Ctrl+V)
  onGanttPasteTasks,
  // Gantt due date change handler (for diamond drag)
  onGanttDueDateChange,
  
  // TodoList-specific
  todoListProps,
  // Todo Fokus mode
  todoFokus,
  // Neural (Astra) Fokus mode
  neuralFokus,
  
  // Nested sprints for actionable filter
  nestedSprints = [],
  allSprints = [],

  /** Task dependency lock map (same source as flow HUD) */
  lockStates = {},
  
  // Bulk actions handlers (from actions)
  bulkActions,
  
  // Empty state arrow indicator
  onShowAddMissionArrow,
  /** Neural floating card: per-view card field visibility (persisted with flow/kanban settings) */
  neuralCardDisplaySettings,
  /** Tasks for Neural graph after nested + neural filter pipeline */
  neuralFilteredTasks,
  neuralViewApiRef,
  boardSelectedIds = null,
  onBoardSelectionChange,
  boardFokus = null,
}) => {
  const docSheetOpen = useDocSheetOpen();
  const handleBoardSelectionChange = useCallback((ids) => {
    const next = ids instanceof Set ? ids : new Set(ids || []);
    // The highlight has to change on this click. The board re-render follows
    // after the browser has painted it.
    paintBoardSelection(document.querySelector('.board-view'), next);
    startTransition(() => {
      onBoardSelectionChange?.(next);
    });
  }, [onBoardSelectionChange]);

  // Get selection state based on current view
  const selectionInfo = useMemo(() => {
    switch (currentView) {
      case 'flow':
        // FlowchartView has its own BulkActionsToolbar, so we don't show it here
        return { selectedIds: new Set(), showToolbar: false };
      case 'kanban':
        return { selectedIds: kanbanSelectedIds || new Set(), showToolbar: true };
      case 'table':
        return { selectedIds: tableSelectedIds || new Set(), showToolbar: true };
      case 'gantt':
        return { selectedIds: ganttSelectedIds || new Set(), showToolbar: true };
      case 'todolist':
        return { selectedIds: todoSelectedIds || new Set(), showToolbar: true };
      case 'board':
        return { selectedIds: boardSelectedIds || new Set(), showToolbar: true };
      case 'neural':
        return { selectedIds: new Set(), showToolbar: false };
      default:
        return { selectedIds: new Set(), showToolbar: false };
    }
  }, [currentView, kanbanSelectedIds, tableSelectedIds, ganttSelectedIds, todoSelectedIds, boardSelectedIds]);

  // Get clear selection handler based on current view
  const handleClearSelection = useCallback(() => {
    switch (currentView) {
      case 'kanban':
        onKanbanSelectionChange?.(new Set());
        break;
      case 'table':
        onTableSelectionChange?.(new Set());
        break;
      case 'gantt':
        onGanttSelectionChange?.(new Set());
        break;
      case 'todolist':
        onTodoSelectionChange?.(new Set());
        break;
      case 'board':
        handleBoardSelectionChange(new Set());
        break;
      default:
        break;
    }
  }, [currentView, onKanbanSelectionChange, onTableSelectionChange, onGanttSelectionChange, onTodoSelectionChange, handleBoardSelectionChange]);

  const selectedTaskIds = Array.from(selectionInfo.selectedIds);
  const selectedCount = selectedTaskIds.length;

  const fokusOn = !!boardFokus?.isFokusMode;
  const fokusTasks = boardFokus?.fokusFilteredTasks;
  const boardTasks = useMemo(() => {
    const pool = fokusOn ? (fokusTasks || []) : (tasks || []);
    const visible = showNestedCampaigns || fokusOn
      ? pool
      : pool.filter((task) => !task.nestedSprintId);
    if (fokusOn) return visible;
    if (!boardHasFilters(boardFilters) && !showActionableOnly) return visible;
    return applyFilters(
      visible,
      boardFilters,
      false,
      lockStates,
      nestedSprints,
      statuses,
      showActionableOnly,
      customFields,
    );
  }, [tasks, showNestedCampaigns, boardFilters, showActionableOnly, lockStates, nestedSprints, statuses, customFields, fokusOn, fokusTasks]);

  // Track view changes for optimistic loading transition
  // We use a "deferred view" pattern: show loading immediately, then render new view after delay
  const [deferredView, setDeferredView] = useState(currentView);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const transitionTimerRef = useRef(null);
  const viewSwitchingRef = useRef(false);
  const prevCampaignIdRef = useRef(undefined);
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;

  // When switching campaigns, align deferred view with the toolbar immediately. Otherwise the
  // deferred view can briefly show the previous mode (e.g. flow) while currentView catches up.
  useLayoutEffect(() => {
    const id = localSprint?.id;
    if (id === prevCampaignIdRef.current) return;
    prevCampaignIdRef.current = id;
    const pending = transitionTimerRef.current;
    if (pending) {
      clearTimeout(pending.outer);
      clearTimeout(pending.inner);
      transitionTimerRef.current = null;
    }
    setDeferredView(currentViewRef.current);
    setIsViewTransitioning(false);
  }, [localSprint?.id]);

  // Minimum time to show loading (allows animation to play smoothly)
  const MIN_LOADING_TIME = 300; // ms - enough for animation to be visible
  
  useEffect(() => {
    console.log('[ViewContainer] Effect: currentView=', currentView, 'deferredView=', deferredView, 'isViewTransitioning=', isViewTransitioning);

    const finishTransition = (view) => {
      viewSwitchingRef.current = false;
      setDeferredView(view);
      setIsViewTransitioning(false);
      window.dispatchEvent(new CustomEvent('viewTransitionComplete', {
        detail: { view }
      }));
    };

    // Campaign switches and cancelled timers can land here with the flag still
    // true. The completion timeout was cleared, so nothing else turns the loader off.
    if (deferredView === currentView) {
      if (viewSwitchingRef.current) finishTransition(currentView);
      return undefined;
    }

    console.log('[ViewContainer] View changed, starting transition');
    viewSwitchingRef.current = true;
    setIsViewTransitioning(true);

    // Dispatch event to notify that a view transition is starting
    // This allows useInitialNavigation to know not to dispatch focusMission yet
    window.dispatchEvent(new CustomEvent('viewTransitionStart', {
      detail: { fromView: deferredView, toView: currentView }
    }));

    // After minimum time, update the deferred view and hide loading
    // The setTimeout(0) inside allows the loading animation to start before heavy render
    const handles = { outer: 0, inner: 0 };
    handles.outer = setTimeout(() => {
      handles.inner = setTimeout(() => {
        if (transitionTimerRef.current !== handles) return;
        console.log('[ViewContainer] Transition complete, setting deferredView to', currentView);
        transitionTimerRef.current = null;
        finishTransition(currentView);
      }, 0);
    }, MIN_LOADING_TIME);
    transitionTimerRef.current = handles;

    return () => {
      clearTimeout(handles.outer);
      clearTimeout(handles.inner);
      if (transitionTimerRef.current === handles) transitionTimerRef.current = null;
    };
  }, [currentView, deferredView]);

  // Render the appropriate view (use deferredView to allow loading animation to play)
  const renderView = () => {
    switch (deferredView) {
      case 'flow':
        return <FlowchartView {...flowProps} onShowAddMissionArrow={onShowAddMissionArrow} />;
        
      case 'kanban':
        return <KanbanView {...kanbanProps} />;
        
      case 'table':
        return <TableView {...tableProps} onShowAddMissionArrow={onShowAddMissionArrow} />;
        
      case 'gantt': {
        // Use FOKUS filtered tasks when in FOKUS mode
        const ganttTasks = (ganttFokus?.isFokusMode ? ganttFokus.fokusFilteredTasks : tasks)
          .filter((t) => t.nodeType !== 'milestone' && t.nodeType !== 'note');
        return (
          <GanttView
            tasks={ganttTasks}
            statuses={statuses}
            allTags={allTags}
            customFields={customFields}
            nestedSprints={nestedSprints}
            localSprint={localSprint}
            allSprints={allSprints}
            campaignId={localSprint?.id || ''}
            campaignName={localSprint?.name || ''}
            campaignIcon={localSprint?.icon}
            campaignProgress={calculatedProgress}
            breadcrumbPath={breadcrumbPath}
            onSelectSprint={onSelectSprint}
            onSprintIconChange={onSprintIconChange}
            onRenameSprint={onRenameSprint}
            onTaskClick={onOpenDetails}
            onTaskDateChange={(taskId, newStart, deltaDaysOrDuration, dragType) => {
              // Gantt drag operations - update start date and/or estimated days
              onTaskDateRangeChange(taskId, newStart, deltaDaysOrDuration, dragType);
            }}
            onDueDateChange={onGanttDueDateChange}
            onAddTask={onGanttAddTask}
            onAddDecision={onGanttAddDecision}
            onAddWait={onGanttAddWait}
            onDecisionAnswerChange={onGanttDecisionAnswerChange}
            onDecisionOptionsChange={onGanttDecisionOptionsChange}
            onRenameTask={onGanttRenameTask}
            onDeleteTasks={onGanttDeleteTasks}
            onPasteTasks={onGanttPasteTasks}
            swimlaneSetting={ganttSwimlane}
            sortSetting={ganttSortSetting}
            showActionableOnly={showActionableOnly}
            showArchived={showArchived}
            filters={ganttFilters}
            selectedTaskIds={ganttSelectedIds}
            onSelectionChange={onGanttSelectionChange}
            isFokusMode={ganttFokus?.isFokusMode}
          />
        );
      }
        
      case 'todolist': {
        // Use FOKUS filtered tasks when in FOKUS mode
        const todoTasks = (todoFokus?.isFokusMode ? todoFokus.fokusFilteredTasks : todoListProps.tasks)
          .filter((t) => t.nodeType !== 'milestone' && t.nodeType !== 'note');
        return <TodoListView {...todoListProps} tasks={todoTasks} isFokusMode={todoFokus?.isFokusMode} onShowAddMissionArrow={onShowAddMissionArrow} />;
      }

      case 'board':
        return (
          <BoardView
            tasks={boardTasks}
            statuses={statuses}
            onOpenDetails={onOpenDetails}
            flowProps={flowProps}
            lockStates={lockStates}
            selectedIds={boardSelectedIds || new Set()}
            onSelectionChange={handleBoardSelectionChange}
            fokus={boardFokus?.isFokusMode ? {
              active: true,
              focusedCount: boardFokus.primaryFokusedIds?.size || 0,
              totalVisibleCount: boardFokus.fokusFilteredTasks?.length || 0,
              onExit: boardFokus.exitFokusMode,
            } : null}
          />
        );

      case 'neural': {
        // FOKUS narrows the graph to the target + its incomplete upstream chain + 1 downstream.
        const neuralTasks = (neuralFokus?.isFokusMode ? neuralFokus.fokusFilteredTasks : (neuralFilteredTasks ?? tasks))
          .filter((t) => t.nodeType !== 'milestone' && t.nodeType !== 'note');
        return (
          <NeuralView
            tasks={neuralTasks}
            statuses={statuses}
            sprintName={localSprint?.name || ''}
            sprintIcon={localSprint?.icon}
            sprintProgress={calculatedProgress}
            breadcrumbPath={breadcrumbPath}
            onSelectSprint={onSelectSprint}
            onRename={onRenameSprint}
            onIconChange={onSprintIconChange}
            lockStates={lockStates}
            nestedSprints={nestedSprints}
            allSprints={allSprints}
            campaignId={localSprint?.id || ''}
            kanbanProps={kanbanProps}
            cardDisplaySettings={neuralCardDisplaySettings}
            viewApiRef={neuralViewApiRef}
            selectedTaskIds={neuralSelectedIds}
            onSelectionChange={onNeuralSelectionChange}
            isFokusMode={neuralFokus?.isFokusMode}
          />
        );
      }

      default:
        return null;
    }
  };

  // Show loading overlay when switching tabs or views
  if (isLoadingContent || (isViewTransitioning && deferredView !== currentView)) {
    return (
      <div className="view-container__loading">
        <LogoLoader size={60} />
      </div>
    );
  }

  // A doc sheet is open over the campaign — unmount the active view entirely
  // so flow/neural/gantt observers, rAF loops and resize work can't compete
  // with typing in the editor (the isolated perf harness had none of this).
  if (docSheetOpen) {
    return <div className="view-container__suspended" aria-hidden="true" />;
  }

  return (
    <>
      {renderView()}
      
      {/* Shared BulkActionsToolbar for non-flow views */}
      {selectionInfo.showToolbar && bulkActions && (
        <BulkActionsToolbar
          selectedCount={selectedCount}
          selectedTaskIds={selectedTaskIds}
          tasks={tasks}
          statuses={statuses}
          allTags={allTags}
          customFields={customFields}
          onStatusChange={bulkActions.onStatusChange}
          onPriorityChange={bulkActions.onPriorityChange}
          onIconChange={bulkActions.onIconChange}
          onSetPrimaryTag={bulkActions.onSetPrimaryTag}
          onAddTag={bulkActions.onAddTag}
          onRemoveTag={bulkActions.onRemoveTag}
          onArchiveTasks={bulkActions.onArchiveTasks}
          onUnarchiveTask={bulkActions.onUnarchiveTask}
          onDueDateChange={bulkActions.onDueDateChange}
          onEstimatedDurationMinutesChange={bulkActions.onEstimatedDurationMinutesChange}
          onStartDateChange={bulkActions.onStartDateChange}
          onCustomFieldValueChange={bulkActions.onCustomFieldValueChange}
          onClearSelection={handleClearSelection}
        />
      )}
    </>
  );
};

export default ViewContainer;
